import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * Synchronizes specific parts of the Redux store with the URL query parameters.
 *
 * This hook listens to changes in a selected portion of Redux state (via the `selector`)
 * and updates the browser's URL query string to match, using the `mapToParams` mapping function.
 *
 * @function useSyncUrlParams
 * @param {object} options - Hook configuration.
 * @param {Function} options.selector - A function that selects a portion of Redux state.
 *   It receives the entire Redux state and should return an object of values to track.
 *   Example: `(state) => state.statspro.statsLeaders`
 * @param {Function} options.mapToParams - A function that maps the selected state values
 *   into a key-value object representing URL parameters.
 *   Example: `(items) => ({ category: items.nav.category, roundId: items.nav.round.id, dataType: items.dataType.type })`
 * @param {boolean} options.isDisabled - used to conditionally run this hook
 *
 * @example
 * useSyncUrlParams({
 *   selector: (state) => state.statspro.statsLeaders,
 *   mapToParams: (items) => ({
 *     category: items.nav.category,
 *     roundId: items.nav.round.id,
 *     dataType: items.dataType.type,
 *   }),
 * });
 */

export const useSyncUrlParams = ({ selector, mapToParams, isDisabled }) => {
    const values = useSelector(selector);
    const previousValues = useRef(values);

    useEffect(() => {
        if (!isDisabled) {
            const params = new URLSearchParams(globalThis.location.search);
            const mapped = mapToParams(values);

            let changed = false;

            Object.entries(mapped).forEach(([key, value]) => {
                const stringValue = value ? String(value) : null;
                const current = params.get(key);

                if (!stringValue) {
                    if (current !== null) {
                        params.delete(key);
                        changed = true;
                    }
                } else if (current !== stringValue) {
                    params.set(key, stringValue);
                    changed = true;
                }
            });

            if (changed) {
                const hash = globalThis.location.hash;
                const newUrl = `${
                    globalThis.location.pathname
                }?${params.toString()}${hash}`;
                globalThis.history.replaceState({}, '', newUrl);
            }

            previousValues.current = values;
        }
    }, [values, mapToParams, isDisabled]);
};
